import { Component, HostListener, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import {
  ButtonComponent,
  ModalComponent,
  ModalV2Component,
  SmallCardComponent,
} from '@shared/components'
import { TimerComponent } from '@src/app/inbetween/components/timer/timer.component'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { FormsModule } from '@angular/forms'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { AccountService, WebSocketService } from '@core/services'
import { CardDetail } from '@core/interfaces'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ModeEnum } from '@core/enums'
import { DealerHttp, GameHttp } from '@core/https'
import { ActivatedRoute, Router } from '@angular/router'
import { DealerDto, DealerLoggerDto, PotBonusDto, UpdateDealerTableDto } from '@core/dtos'
import { SmSpinnerComponent } from '@shared/components/sm-spinner/sm-spinner.component'
import { faPlusSquare, faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { InfusePotModalComponent } from './infuse-pot-modal/infuse-pot-modal.component'
import { SvgIconComponent } from 'angular-svg-icon'
import { CamelCaseToTitlePipe } from '@shared/pipes'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { CardTypeEnum } from '@shared/enums'
import { SmallVerticalCardComponent } from '@shared/components/cards/small-vertical-card/small-vertical-card.component'

type CardType = { label: string; value: CardTypeEnum; icon: string }

@Component({
  selector: 'app-game-panel',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    ModalComponent,
    TimerComponent,
    FontAwesomeModule,
    FormsModule,
    SmSpinnerComponent,
    InfusePotModalComponent,
    SvgIconComponent,
    MatProgressSpinnerModule,
    CamelCaseToTitlePipe,
    ModalV2Component,
    SmallCardComponent,
    SmallVerticalCardComponent,
  ],
  templateUrl: './game-panel.component.html',
  styleUrl: './game-panel.component.sass',
  providers: [WebSocketService],
})
export class GamePanelComponent implements OnInit {
  readonly loggerId = this._accountService.currentUserId

  faPlusSquare = faPlusSquare
  faCheckCircle = faCheckCircle

  cardTypes: CardType[] = [
    { label: 'Diamond', value: CardTypeEnum.DIAMOND, icon: '◆' },
    { label: 'Heart', value: CardTypeEnum.HEART, icon: '♥' },
    { label: 'Spade', value: CardTypeEnum.SPADE, icon: '♠' },
    { label: 'Flower', value: CardTypeEnum.FLOWER, icon: '♣' },
  ]

  cards: CardDetail[] = []
  filteredCards: CardDetail[] = []
  selectedCards: CardDetail[] = []
  submittedCards: CardDetail[] = []
  selectedFilter = ''
  isAsc: boolean = false
  allSelectedCards: CardDetail[] = []

  searchTerm: string = ''

  showCardConfirmationModal: boolean = false
  showInjectPotModal = false
  currentSeatNo: number
  mode: ModeEnum
  countdown: number
  modeType = ModeEnum
  dealerId: string
  showSpinner: boolean = false
  totaPot = 0
  infusionAmount = 0
  selectedDealer: DealerDto
  isSmallScreen: boolean

  get selectedLength(): number {
    return this.mode === ModeEnum.PlayerCards ? 2 : this.mode === ModeEnum.MaverickCard ? 1 : 0
  }

  get sortIcon(): IconProp {
    return this.isAsc ? 'arrow-up-short-wide' : 'arrow-down-short-wide'
  }

  get playPauseIcon(): string {
    return this.mode === ModeEnum.Paused
      ? 'assets/svg/icons/Play.svg'
      : 'assets/svg/icons/Pause.svg'
  }

  get canPickCard(): boolean {
    return (
      this.mode !== ModeEnum.Setup &&
      this.mode !== ModeEnum.BuyInOpen &&
      this.mode !== ModeEnum.PlayerBets &&
      this.mode !== ModeEnum.Result &&
      this.mode !== ModeEnum.Paused
    )
  }

  get modeDescription(): string {
    switch (this.mode) {
      case ModeEnum.Setup:
        return 'SETUP'
      case ModeEnum.BuyInOpen:
        return 'BUY IN OPEN'
      case ModeEnum.PlayerCards:
        return 'SELECT SEAT #' + this.currentSeatNo + ' CARDS'
      case ModeEnum.MaverickCard:
        return 'SELECT MAVERICK CARD'
      case ModeEnum.Result:
        return 'RESULT'
      case ModeEnum.PlayerBets:
        return 'PLAYER BETTING'
      case ModeEnum.Paused:
        return 'GAME PAUSED'
      default:
        return ''
    }
  }

  get wrongSubmissionMessage(): string {
    return this.mode === ModeEnum.PlayerCards
      ? 'PLAYER'
      : this.mode === ModeEnum.MaverickCard
        ? 'MAVERICK'
        : ''
  }

  get showPotButton(): boolean {
    return this.mode === ModeEnum.Setup
  }

  constructor(
    private _webSocketService: WebSocketService,
    private _snackBar: MatSnackBar,
    private _gameHttp: GameHttp,
    private _activatedroute: ActivatedRoute,
    private _router: Router,
    private _dealerHttp: DealerHttp,
    private _accountService: AccountService,
  ) {
    this.isSmallScreen = window.innerWidth <= 768
  }

  ngOnInit(): void {
    this.dealerId = this._activatedroute.snapshot.paramMap.get('dealerId') as string

    this._webSocketService.initializeConfig(this.dealerId)
    this.getCardsDetails()
    this.listenToSocket()
    this.filteredCards = this.cards
    this._gameHttp.getLoggerInitData(this.dealerId).subscribe((i) => {
      this.mode = i.config.mode
      this.totaPot = i.currentTotalPot
      this.currentSeatNo = i.config.seatNo
      this.selectedDealer = i.dealer
    })
  }

  listenToSocket() {
    this._webSocketService.listenToMode(this.dealerId, ({ mode, currentSeatNo }) => {
      this.mode = mode
      this.currentSeatNo = currentSeatNo

      if (mode === ModeEnum.Setup || mode === ModeEnum.Result) {
        this.cards = []
        this.getCardsDetails()

        this.filteredCards = this.cards
      }
    })

    this._webSocketService.listenToCountdown(this.dealerId, (timeRemaining: number) => {
      this.countdown = timeRemaining
    })

    this._webSocketService.listenToPotBonusUpdate(this.dealerId, (i: PotBonusDto) => {
      this.totaPot = i.amount
    })
  }

  startGame() {
    this._webSocketService.emitBuyInOpen(this.dealerId)
  }

  async backToGamePanelMenu() {
    const data = {
      hasLogger: false,
      loggerId: null,
      dealerId: this.dealerId,
    } as DealerLoggerDto

    this.showSpinner = true
    await this._dealerHttp.updateDealerLogger(data)
    this.showSpinner = false
    this._router.navigate(['game-menu', this.loggerId])
  }

  handleConfirmation() {
    this.filteredCards = this.filteredCards.filter((card) => !this.submittedCards.includes(card))
    let message = ''

    if (this.mode === ModeEnum.PlayerCards) {
      message = `Select Player Cards`
      this._webSocketService.emitPlayerCardsConfirmation({
        isConfirm: true,
        dealerId: this.dealerId,
        cards: this.allSelectedCards,
      })
    } else if (this.mode === ModeEnum.MaverickCard) {
      message = `Select Maverick Card`

      this._webSocketService.emitMaverickCardConfirmation({
        isConfirm: true,
        dealerId: this.dealerId,
        seatNo: this.currentSeatNo,
        cards: this.allSelectedCards,
      })
    }

    this._snackBar.open(message, 'Dismiss', {
      duration: 2000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    })

    this.showCardConfirmationModal = false
  }

  toggleGamePaused(): void {
    this._webSocketService.emitPuaseGame(this.dealerId)
  }

  filterCards(cardType: string) {
    this.selectedFilter = cardType

    if (cardType) {
      this.filteredCards = this.cards.filter((card) => card.cardType === cardType)
      return
    }
    this.filteredCards = this.cards
  }

  sortCards(): void {
    this.isAsc = !this.isAsc
    this.filteredCards.sort((a, b) => {
      if (this.isAsc) {
        return a.number - b.number
      } else {
        return b.number - a.number
      }
    })
  }

  selectCard(card: CardDetail): void {
    const index = this.selectedCards.findIndex((selectedCard) => selectedCard === card)

    if (index !== -1) {
      this.selectedCards.splice(index, 1)
    } else {
      if (this.selectedCards.length >= this.selectedLength) {
        this.selectedCards.shift()
      }

      this.selectedCards.push(card)
    }
  }

  isSelected(card: CardDetail): boolean {
    return this.selectedCards.includes(card)
  }

  getCardsDetails(): void {
    const labelMap: { [key: number]: string } = {
      1: 'A',
      11: 'J',
      12: 'Q',
      13: 'K',
    }

    this.cardTypes.forEach((cardType) => {
      for (let i = 1; i <= 13; i++) {
        const label = labelMap[i] || `${i}`
        this.cards.push({
          cardType: cardType.value,
          label: `${label} - ${cardType.value}`,
          number: i,
        })
      }
    })
  }

  submitCards(): void {
    if (this.mode === ModeEnum.PlayerCards) {
      this._webSocketService.emitPlayerCards({ cards: this.selectedCards, dealerId: this.dealerId })
      this.allSelectedCards = this.selectedCards
    }

    if (this.mode === ModeEnum.MaverickCard) {
      this._webSocketService.emitMaverickCard({
        cards: this.selectedCards,
        dealerId: this.dealerId,
      })

      this.allSelectedCards = this.allSelectedCards.concat(this.selectedCards)
    }
    this.showCardConfirmationModal = true

    this.submittedCards = this.selectedCards

    this.selectedCards = []
  }

  search(): void {
    this.filteredCards = this.cards.filter((card) => {
      const cardLabel = card.label.toLowerCase()
      return cardLabel.includes(this.searchTerm.toLowerCase())
    })
  }

  preventClose(event: MouseEvent) {
    event.stopPropagation()
  }

  onClickiInfusePot() {
    this.showInjectPotModal = true
  }

  @HostListener('window:keydown.control.space', ['$event'])
  gamePause(event: KeyboardEvent) {
    event.preventDefault()
    this.toggleGamePaused()
  }

  @HostListener('window:keydown.control.enter', ['$event'])
  cardSubmit(event: KeyboardEvent) {
    event.preventDefault()
    if (this.selectedCards.length === 2) {
      this.submitCards()
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.isSmallScreen = window.innerWidth <= 768
  }
}
